import { cosignErrorCode } from '@constants'
import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { useBeforeunload } from '@dni-react-hooks/use-beforeunload'
import { Alert } from '@pets-ui/Alert/Alert'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { ArrowLeftSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import * as stylex from '@stylexjs/stylex'
import { isEmail } from '@utils'
import { type AxiosError } from 'axios'
import { format } from 'date-fns'
import queryString from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEffectOnce } from '../../../hooks/useEffectOnce'
import { useOtpCosignSend } from '../../../hooks/useOtpCosignSend'
import { useTimer } from '../../../hooks/useTimer'
import { type SendOtpErrorResponse } from '../../../types/apiErrorResponse'
import { type Otp } from '../../../types/dataModel/platformSdk'
import { getAccount } from '../../../utils/cookie'
import { LoadingProvider } from '../../LoadingProvider'
import { useContextCosign } from '../../useContextCosign'
import { LoginLink } from '../LoginLink'
import { Service } from '../Service'
import { CardTitleWithIcon } from './CardTitleWithIcon'
import { DividerOr } from './DividerOr'
import { InputVerification } from './InputVerification'
import { LoginStep } from './types'
import { useContextLogin } from './useContextLogin'

const styles = stylex.create({
  subTitleContainer: {
    gap: '4px',
    alignItems: 'center',
    color: '#4B4B4B',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: '20px',
    marginTop: '20px',
  },
  validateCodeContainer: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  resendContainer: {
    gap: '4px',
    display: 'flex',
  },
  validateCodeTitle: {
    color: '#4B4B4B',
  },
  validateResendText: {
    color: '#AAAAAA',
  },
  buttonBack: {
    margin: '0 auto',
    color: '#7A7A7A',
    display: 'block',
    marginTop: '12px',
  },
})

export function ValidationForgetPassword() {
  const { t } = useTranslation()
  const { setStep, backStep } = useContextLogin()
  const { tokenLoginUrl, redirectUrl, fallbackUrl } = useContextCosign()
  const { startTimer, seconds, isCounting } = useTimer()
  const [remainingTimes, setRemainingTimes] = useState(0)
  const [inputVerificationError, setInputVerificationError] = useState('')
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const { forgetPasswordSendOtp, isMutating, data } = useOtpCosignSend({ tokenLoginUrl, redirectUrl, fallbackUrl })
  const search = queryString.parse(window.location.search)
  const account = getAccount()
  const type: Otp['type'] = account && isEmail(account ?? '') ? 'email' : 'sms'
  const typeDisplay = type === 'email' ? t('login.sendEmailTitle') : t('login.sendSmsTitle')

  const canSendOtp = useRef(true)
  const handleSendOtp = useCallback(async () => {
    if (!account) return
    try {
      canSendOtp.current = false
      const { data } = await forgetPasswordSendOtp(type)
      startTimer(data.expiredAt)
      setRemainingTimes(data.remainingTimes)
      if (data.remainingTimes < 5) {
        if (type === 'email')sendEvent({ eventName: 'Email OTP resend', eventParams: { event_category: 'Email OTP', email_otp_resend_clicks: 5 - data.remainingTimes } })
        if (type === 'sms')sendEvent({ eventName: 'mobile OTP resend', eventParams: { event_category: 'Mobile OTP', mobile_otp_resend_clicks: 5 - data.remainingTimes } })
      }
    }
    catch (error) {
      console.log(error)
      const { response } = error as AxiosError<SendOtpErrorResponse>
      if (response?.data?.code === cosignErrorCode.OTP_RESEND_LIMIT_REACHED || response?.data?.code === cosignErrorCode.OTP_RESEND_LIMIT_REACHED_2) {
        startTimer(response.data.detail.expiredAt)
        setRemainingTimes(response.data.detail.remainingTimes ?? 0)
        if (type === 'email')sendEvent({ eventName: 'Email OTP resend limit reached', eventParams: { event_category: 'Email OTP' } })
        if (type === 'sms')sendEvent({ eventName: 'mobile OTP resend limit reached', eventParams: { event_category: 'Mobile OTP' } })
      }
    }
  }, [account, forgetPasswordSendOtp, sendEvent, startTimer, type])

  useEffect(() => {
    if (!canSendOtp.current || search?.otpCode) return
    handleSendOtp()
  }, [handleSendOtp, search?.otpCode])

  const handleResendOtp = useCallback(() => {
    handleSendOtp()
    setInputVerificationError('')
  }, [handleSendOtp])

  const shouldShowRemainingTimesAlert = remainingTimes != null && seconds !== 0 && (remainingTimes >= 0 && remainingTimes < 5)
  const shouldShowResendText = remainingTimes !== 0 || seconds === 0

  const handleClickBackToLogin = () => {
    setStep(LoginStep.LOGIN)
    if (type === 'email') {
      sendEvent({ eventName: 'Email OTP back to login', eventParams: { event_category: 'Email OTP' } })
      sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
    }
    if (type === 'sms') {
      sendEvent({ eventName: 'mobile OTP back to login', eventParams: { event_category: 'Mobile OTP' } })
      sendEvent({ eventName: 'mobile OTP drop', eventParams: { event_category: 'Mobile OTP' } })
    }
  }

  const handleClickBack = () => {
    backStep()
    if (type === 'email') {
      sendEvent({ eventName: 'Email OTP return', eventParams: { event_category: 'Email OTP' } })
      sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
    }
    if (type === 'sms') {
      sendEvent({ eventName: 'mobile OTP return', eventParams: { event_category: 'Mobile OTP' } })
      sendEvent({ eventName: 'mobile OTP drop', eventParams: { event_category: 'Mobile OTP' } })
    }
  }

  useEffectOnce(() => {
    if (type === 'email') sendEvent({ eventName: 'Email OTP started', eventParams: { event_category: 'Email OTP' } })
    if (type === 'sms') sendEvent({ eventName: 'mobile OTP started', eventParams: { event_category: 'Mobile OTP' } })
  })

  useBeforeunload(() => {
    if (type === 'email') sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
    if (type === 'sms') sendEvent({ eventName: 'mobile OTP drop', eventParams: { event_category: 'Mobile OTP' } })
  })

  return (
    <LoadingProvider loading={(!data && !!search?.otpCode) || isMutating}>
      <CardTitleWithIcon
        title={t('login.forgetPassword')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={handleClickBack}
      />
      <div {...stylex.props(styles.subTitleContainer)}>
        <Text variant="body">{t('login.sendVerifyCodeTo')}</Text>
        <Text variant="h5">{account}</Text>
      </div>
      <div {...stylex.props(styles.validateCodeContainer)}>
        <Text variant="caption" rootStyle={styles.validateCodeTitle}>{typeDisplay}</Text>
        <InputVerification error={inputVerificationError} setError={setInputVerificationError} purpose="forget" />
        {shouldShowResendText && (
          <div {...stylex.props(styles.resendContainer)}>
            <Text variant="caption" rootStyle={styles.validateResendText}>
              {t('login.didntReceiveVerificationCode')}
              {seconds > 0 ? `(${seconds}s)` : ''}
            </Text>
            <LoginLink disabled={isCounting} onClick={handleResendOtp}>{t('login.resend')}</LoginLink>
          </div>
        )}
        {shouldShowRemainingTimesAlert
        && (
          <Alert type="warning">
            <Text variant="caption">
              {t('login.resendErmainingTimesContent', { remainingTimes })}
              {remainingTimes === 0 && `${t('login.resendWaitContent')}(${format(seconds * 1000, 'mm:ss')})`}
            </Text>
          </Alert>
        )}
      </div>
      <DividerOr />
      <ButtonBase rootStyle={styles.buttonBack} onClick={handleClickBackToLogin}><Text variant="body">{t('login.returnToLogin')}</Text></ButtonBase>
      <Service />
    </LoadingProvider>
  )
}
